import { useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Check, X, Pencil, AlertTriangle, FileUp,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import type { PolicySectionReview } from '@/types/api';

function confidenceColor(c: number): string {
  if (c >= 0.8) return 'bg-compliance-low/15 text-compliance-low border-compliance-low/30';
  if (c >= 0.6) return 'bg-yellow-500/15 text-yellow-600 border-yellow-500/30';
  return 'bg-compliance-critical/15 text-compliance-critical border-compliance-critical/30';
}

function confidenceLabel(c: number): string {
  if (c >= 0.8) return `${Math.round(c * 100)}% confident`;
  if (c >= 0.6) return `${Math.round(c * 100)}% — review recommended`;
  return `${Math.round(c * 100)}% — human review required`;
}

export function PolicyReviewPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const [showWriteBackModal, setShowWriteBackModal] = useState(false);
  const [writeBackDone, setWriteBackDone] = useState(false);

  const closeModal = useCallback(() => setShowWriteBackModal(false), []);
  const modalRef = useFocusTrap(showWriteBackModal, closeModal);

  const { data, isLoading } = useQuery({
    queryKey: ['policy-review-session', sessionId],
    queryFn: () => api.policyReview.session(sessionId!),
    enabled: !!sessionId && !!user,
  });

  const session = data?.session;
  const sections = data?.sections ?? [];

  const reviewedSections = sections.filter((s) => s.status !== 'pending');
  const acceptedCount = sections.filter((s) => s.status === 'accepted' || s.status === 'edited').length;
  const rejectedCount = sections.filter((s) => s.status === 'rejected').length;
  const allReviewed = sections.length > 0 && reviewedSections.length === sections.length;

  const writeBackMutation = useMutation({
    mutationFn: () => api.policyReview.writeBack(sessionId!),
    onSuccess: () => {
      setWriteBackDone(true);
      setShowWriteBackModal(false);
      toast.success('Document updated successfully');
      queryClient.invalidateQueries({ queryKey: ['policy-review-session', sessionId] });
    },
    onError: () => toast.error('Write-back failed — please try again'),
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal border-t-transparent" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate('/policy-review')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <p className="mt-4 text-muted-foreground">Session not found.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="shrink-0 border-b border-border p-6 pb-4">
        <div className="mb-3 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/policy-review')}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Policy Reviews
          </Button>
        </div>

        <PageHeader
          title={session.documentName}
          subtitle={`${sections.length} affected sections identified by AI`}
          actions={
            session.projectId ? (
              <Button variant="outline" size="sm" onClick={() => navigate(`/projects/${session.projectId}`)}>
                View Project
              </Button>
            ) : undefined
          }
        />

        <div className="mt-3 flex items-center gap-3">
          <Progress
            value={sections.length > 0 ? (reviewedSections.length / sections.length) * 100 : 0}
            className="h-2 flex-1"
          />
          <span className="shrink-0 text-xs text-muted-foreground">
            {reviewedSections.length} of {sections.length} reviewed
          </span>
        </div>
      </div>

      {/* Write-back success banner */}
      {writeBackDone && (
        <div className="shrink-0 border-b border-compliance-low/30 bg-compliance-low/10 px-6 py-3">
          <div className="flex items-center gap-2 text-sm font-medium text-compliance-low">
            <CheckCircle2 className="h-4 w-4" />
            Document updated successfully
          </div>
          <div className="mt-1 flex gap-4 text-xs">
            <Link to="/audit-trail" className="text-teal hover:underline">View audit trail</Link>
            {session.projectId && (
              <Link to={`/projects/${session.projectId}`} className="text-teal hover:underline">Return to project</Link>
            )}
          </div>
        </div>
      )}

      {/* Section cards */}
      <div className="flex-1 space-y-4 overflow-y-auto p-6">
        {sections.map((section) => (
          <SectionCard
            key={section.reviewId}
            section={section}
            sessionId={sessionId!}
            userName={user?.name ?? 'User'}
          />
        ))}
      </div>

      {/* Sticky bottom bar */}
      {reviewedSections.length > 0 && !writeBackDone && (
        <div className="shrink-0 border-t border-border bg-sidebar px-6 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {reviewedSections.length} of {sections.length} sections reviewed
              {acceptedCount > 0 && ` (${acceptedCount} accepted`}
              {rejectedCount > 0 && `, ${rejectedCount} rejected`}
              {(acceptedCount > 0 || rejectedCount > 0) && ')'}
            </span>
            <Button
              className="bg-teal text-white hover:bg-teal/90"
              disabled={!allReviewed || writeBackMutation.isPending}
              onClick={() => setShowWriteBackModal(true)}
              aria-label="Write changes back to source document"
            >
              <FileUp className="mr-2 h-4 w-4" />
              {writeBackMutation.isPending ? 'Writing back...' : 'Write Back'}
            </Button>
          </div>
        </div>
      )}

      {/* Write-back confirmation modal (F4: focus trap) */}
      {showWriteBackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={closeModal}>
          <div
            ref={modalRef}
            role="dialog"
            aria-label="Confirm write-back"
            aria-modal="true"
            className="mx-4 w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold">Confirm Write-Back</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              This will update <span className="font-medium text-foreground">{session.documentName}</span> via the connected source.
              A timestamped backup will be created before any changes are made.
            </p>
            <div className="mt-1 text-xs text-muted-foreground">
              {acceptedCount} section{acceptedCount !== 1 ? 's' : ''} will be updated.
              {rejectedCount > 0 && ` ${rejectedCount} section${rejectedCount !== 1 ? 's' : ''} will keep original text.`}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={closeModal} aria-label="Cancel write-back">Cancel</Button>
              <Button
                className="bg-teal text-white hover:bg-teal/90"
                onClick={() => writeBackMutation.mutate()}
                disabled={writeBackMutation.isPending}
                aria-label="Confirm and write back changes"
              >
                {writeBackMutation.isPending ? 'Writing...' : 'Confirm Write-Back'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section Card ─────────────────────────────────────────

function SectionCard({
  section,
  sessionId,
  userName,
}: {
  readonly section: PolicySectionReview;
  readonly sessionId: string;
  readonly userName: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(section.proposedText);
  // G4: Mobile tab state for responsive diff
  const [mobileTab, setMobileTab] = useState<'current' | 'proposed'>('proposed');

  const acceptMutation = useMutation({
    mutationFn: () => api.policyReview.acceptSection(section.reviewId),
    onSuccess: () => {
      toast.success(`"${section.sectionTitle}" accepted`);
      queryClient.invalidateQueries({ queryKey: ['policy-review-session', sessionId] });
    },
    onError: () => toast.error('Failed to accept section'),
  });

  const editMutation = useMutation({
    mutationFn: (finalText: string) => api.policyReview.editSection(section.reviewId, finalText),
    onSuccess: () => {
      setEditing(false);
      toast.success(`"${section.sectionTitle}" saved with edits`);
      queryClient.invalidateQueries({ queryKey: ['policy-review-session', sessionId] });
    },
    onError: () => toast.error('Failed to save edit'),
  });

  const rejectMutation = useMutation({
    mutationFn: () => api.policyReview.rejectSection(section.reviewId),
    onSuccess: () => {
      toast.success(`"${section.sectionTitle}" rejected — original preserved`);
      queryClient.invalidateQueries({ queryKey: ['policy-review-session', sessionId] });
    },
    onError: () => toast.error('Failed to reject section'),
  });

  const isReviewed = section.status !== 'pending';
  const borderColor = isReviewed
    ? section.status === 'rejected' ? 'border-muted-foreground/30' : 'border-compliance-low/40'
    : 'border-border';

  return (
    <Card className={cn('transition-colors', borderColor)}>
      <CardContent className="p-0">
        {/* Section header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">{section.sectionTitle}</h3>
            <Badge variant="outline" className={cn('text-[10px]', confidenceColor(section.confidence))}>
              {confidenceLabel(section.confidence)}
            </Badge>
          </div>
          {isReviewed && (
            <Badge variant="outline" className={cn('text-[10px]', section.status === 'rejected' ? 'bg-muted text-muted-foreground' : 'bg-compliance-low/10 text-compliance-low')}>
              {section.status === 'accepted' && `Accepted by ${userName}`}
              {section.status === 'edited' && `Edited by ${userName}`}
              {section.status === 'rejected' && 'No changes — original preserved'}
            </Badge>
          )}
        </div>

        {/* Ambiguity warning */}
        {section.confidence < 0.6 && (
          <div className="flex items-center gap-2 border-b border-border bg-compliance-critical/5 px-4 py-2 text-xs text-compliance-critical">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            Ambiguous regulation — human review required
          </div>
        )}

        {/* G4: Desktop two-column diff (≥768px) */}
        <div className="hidden md:grid md:grid-cols-2 md:divide-x md:divide-border">
          <div className="p-4">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Current Version</div>
            <pre className="whitespace-pre-wrap text-xs leading-relaxed text-foreground/80">{section.originalText}</pre>
          </div>
          <div className="p-4">
            <div className="mb-2 flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-teal">{editing ? 'Your Edit' : 'AI Proposed'}</span>
              {!editing && <Badge variant="outline" className="bg-purple-100/50 text-[9px] text-purple-700 border-purple-200">AI Draft</Badge>}
            </div>
            {editing ? (
              <EditArea editText={editText} setEditText={setEditText} editMutation={editMutation} onCancel={() => { setEditing(false); setEditText(section.proposedText); }} />
            ) : (
              <pre className="whitespace-pre-wrap text-xs leading-relaxed text-foreground/80">{section.finalText ?? section.proposedText}</pre>
            )}
          </div>
        </div>

        {/* G4: Mobile tabbed single-column diff (<768px) */}
        <div className="md:hidden">
          <div className="border-b border-border px-4 pt-2">
            <Tabs value={mobileTab} onValueChange={(v) => setMobileTab(v as 'current' | 'proposed')}>
              <TabsList variant="line">
                <TabsTrigger value="current">Current Version</TabsTrigger>
                <TabsTrigger value="proposed">{editing ? 'Your Edit' : 'AI Proposed'}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="p-4">
            {mobileTab === 'current' ? (
              <pre className="whitespace-pre-wrap text-xs leading-relaxed text-foreground/80">{section.originalText}</pre>
            ) : editing ? (
              <EditArea editText={editText} setEditText={setEditText} editMutation={editMutation} onCancel={() => { setEditing(false); setEditText(section.proposedText); }} />
            ) : (
              <pre className="whitespace-pre-wrap text-xs leading-relaxed text-foreground/80">{section.finalText ?? section.proposedText}</pre>
            )}
          </div>
        </div>

        {/* AI reasoning */}
        {section.changesSummary && (
          <div className="border-t border-border px-4 py-2">
            <p className="text-xs italic text-muted-foreground">{section.changesSummary}</p>
          </div>
        )}

        {/* Actions */}
        {!isReviewed && !editing && (
          <div className="flex gap-2 border-t border-border px-4 py-3">
            <Button size="sm" className="bg-compliance-low text-white hover:bg-compliance-low/90" onClick={() => acceptMutation.mutate()} disabled={acceptMutation.isPending} aria-label={`Accept changes to ${section.sectionTitle}`}>
              <Check className="mr-1 h-3.5 w-3.5" />Accept
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(true); setMobileTab('proposed'); }} aria-label={`Edit proposed changes to ${section.sectionTitle}`}>
              <Pencil className="mr-1 h-3.5 w-3.5" />Edit
            </Button>
            <Button size="sm" variant="outline" className="text-muted-foreground hover:text-foreground" onClick={() => rejectMutation.mutate()} disabled={rejectMutation.isPending} aria-label={`Reject changes to ${section.sectionTitle}`}>
              <X className="mr-1 h-3.5 w-3.5" />Reject
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EditArea({
  editText,
  setEditText,
  editMutation,
  onCancel,
}: {
  readonly editText: string;
  readonly setEditText: (v: string) => void;
  readonly editMutation: { mutate: (text: string) => void; isPending: boolean };
  readonly onCancel: () => void;
}) {
  return (
    <div className="space-y-2">
      <textarea
        className="h-48 w-full rounded border border-border bg-background p-2 text-xs leading-relaxed focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
        value={editText}
        onChange={(e) => setEditText(e.target.value)}
      />
      <div className="flex gap-2">
        <Button size="sm" className="bg-teal text-white hover:bg-teal/90" onClick={() => editMutation.mutate(editText)} disabled={editMutation.isPending}>
          {editMutation.isPending ? 'Saving...' : 'Save Edit'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
